import React from 'react';

import { NextPageWithLayout } from 'next';

import { InternalLink } from '../../components/Clickables/InternalLink/InternalLink';
import { Container } from '../../components/Container/Container';
import { MetaTags } from '../../components/MetaTags/MetaTags';

export const CookiePolicy: NextPageWithLayout = () => (
  <Container variant="thin">
    <MetaTags
      link="/cookie-policy"
      title="Cookie Policy"
      description="Read the latest Cookie Policy on Fishbrain"
    />
    <div>
      <h1>Cookie Policy</h1>
      <h2>What is a cookie?</h2>
      <p>
        A cookie is a piece of data that may be stored in the browser you use to access Fishbrain,
        which enables features and functionality. It allows our website to “remember” your activity
        or preferences over a length of time or during a particular session. If you&apos;re signed
        in to Fishbrain, cookies allow us to show you the right information and personalize your
        experience.
      </p>
      <p>
        Some of the cookies we use have been developed and deployed to facilitate our provision of
        the service. These are known as first-party cookies. We also utilize third-party cookies,
        which are built by our third-party vendors and employed on our website, and are explained
        further below.
      </p>
      <h2>How does Fishbrain use cookies?</h2>
      <p>
        We use session cookies and persistent cookies. Session cookies are temporary cookies that
        are not stored in your browser beyond the session in question. They are used for
        authentication and security purposes and to ensure the effective and necessary functioning
        of the Fishbrain features which you are utilizing. We may also use a session cookie to
        remember your preferences when viewing the site. These session cookies are erased when you
        exit your browser.
      </p>
      <p>
        Persistent cookies are those placed on your browser for 180 days when you visit this site.
      </p>
      <h3>First-Party Cookies</h3>
      <p>
        We have developed and use cookies via our website to authenticate users, block malicious use
        of login credentials and shield unauthorized access to member data. We also use these
        cookies to collect information on website and app usage in order to understand and improve
        Fishbrain. These cookies also help us learn how well our website and application operate
        across different locations and to identify any issues in the operation and provision of our
        service.
      </p>
      <p>The data we collect is, among other things:</p>
      <ul className="list-inside list-disc">
        <li>Browser type</li>
        <li>Operating System</li>
        <li>Mobile Device Information</li>
        <li>IP address</li>
        <li>Which pages you visited</li>
        <li>How long you spent on the website</li>
        <li>When you visited our website at the latest</li>
        <li>References (where you came from)</li>
      </ul>
      <p>
        For further information on the uniquely identifiable information data collected when using
        the application and our website(s), please see Fishbrain&apos;s applicable{' '}
        <InternalLink to="/privacy">Privacy Policy</InternalLink>.
      </p>
      <h3>Third-Party Cookies</h3>
      <p>
        We also permit the setting of third-party cookies on our website. These assist us in
        measuring and understanding how our product is used and how they can be optimized. We may
        also receive other analytics information from these third parties.
      </p>
      <p>Third party cookies are mainly used for the following purposes:</p>
      <ul className="list-inside list-disc">
        <li>Analyze behavior to develop and improve website design and content;</li>
        <li>Build segments for advertising based on visitor behavior;</li>
        <li>View relevant ads for previous visitors;</li>
        <li>Minimize advertising that is irrelevant to some visitors;</li>
        <li>Test site content and structure to enhance the user experience; and</li>
        <li>Track visitor behavior on different versions of the site.</li>
      </ul>
      <p>
        When you visit our sites, ad cookies will be stored on your computer so we can understand
        where you have been on the site. Our display advertising partner, Google Ads, then lets us
        present ads to you on other websites based on your previous interaction with us. This
        advertising technology does not store personal information such as your name, email address,
        postal address or telephone number.
      </p>
      <h2>Managing and Refusing the Use of Cookies</h2>
      <p>
        Most browsers allow you to choose whether you wish to accept cookies. Different browsers
        make different controls available to you and so we provide links below to popular browser
        instructions on how you can do this. Generally, your browser will offer you the choice to
        accept, refuse or delete all cookies, or third-party cookies on a website, or those from
        specific websites. However, if you restrict our capability to set cookies, you may limit the
        full functionality of Fishbrain, which will make some of our services and features
        unavailable to you.
      </p>
      <ul className="list-inside list-disc">
        <li>
          <a
            href="https://support.google.com/chrome/answer/95647"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Chrome
          </a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/en-ie/help/17442/windows-internet-explorer-delete-manage-cookies"
            target="_blank"
            rel="noopener noreferrer"
          >
            Internet Explorer
          </a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/en-ph/help/4027947/windows-delete-cookies"
            target="_blank"
            rel="noopener noreferrer"
          >
            Edge
          </a>
        </li>
        <li>
          <a
            href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
            target="_blank"
            rel="noopener noreferrer"
          >
            Firefox
          </a>
        </li>
        <li>
          <a
            href="https://support.apple.com/en-gb/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
            target="_blank"
            rel="noopener noreferrer"
          >
            Safari
          </a>
        </li>
        <li>
          <a
            href="https://support.apple.com/en-us/HT201265"
            target="_blank"
            rel="noopener noreferrer"
          >
            Safari Mobile
          </a>
        </li>
        <li>
          <a
            href="https://help.opera.com/en/latest/web-preferences/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Opera
          </a>
        </li>
      </ul>
      <h2>Do Not Track (DNT)</h2>
      <p>
        We may not recognize Do-Not-Track (“DNT”) signals from your web browsers or device. This is
        because there is not yet a universally accepted standard on how to interpret and respond to
        DNT signals, which often conflict with other settings. If and when a standard is
        established, we will re-examine how we can appropriately respond to DNT signals.
      </p>
    </div>
  </Container>
);
